import React, {useState} from 'react';
import {
    Checkbox,
    Toolbar,
    Typography,
    Tooltip,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@material-ui/core';
import {lighten, makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import DeleteIcon from '@material-ui/icons/Delete';
import Show from 'components/utils/Show';
import moment from 'moment'

const useStyles = makeStyles(theme => ({
    highlight:
        theme.palette.type === 'light'
            ? {
                color: theme.palette.secondary.main,
                backgroundColor: lighten(theme.palette.secondary.light, 0.85),
            }
            : {
                color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
            },
    grow: {
        flexGrow: 1,
    },
    title: {
        flex: '1 1 100%',
    },
    numberInput: {
        textAlign: 'right',
    },
    cells: {
        paddingRight: '.5em',
        paddingLeft: '.5em',
    }
}));

const FancyTable = ({
    title = '',
    headers = [],
    value = [],
    onChange,
    children,
    tableStyles,
    dense,
}) => {
    const classes = useStyles();

    // UI state
    const [selected, setSelected] = useState([]);

    const isSelected = index => selected.indexOf(index) >= 0;

    const handleRowClick = (event, inIndex) => {
        const index = selected.indexOf(inIndex);
        let newSelected = [];

        if (index === -1) {
            newSelected = selected.concat(inIndex);
        } else if (index === 0) {
            newSelected = selected.slice(1);
        } else if (index === selected.length - 1) {
            newSelected = selected.slice(0, -1);
        } else {
            newSelected = newSelected.concat(
                selected.slice(0, index),
                selected.slice(index + 1)
            );
        }

        setSelected(newSelected);
    };

    const handleSelectAllClick = event => {
        if (event.target.checked) {
            setSelected(value.map((val, index) => index));
        } else {
            setSelected([]);
        }
    };

    const deleteSelected = () => {
        const newData = value.filter((item, index) => !~selected.indexOf(index));

        onChange(newData);
        setSelected([]);
    };

    return <>
        <Toolbar
            disableGutters
            className={clsx(classes.root, {
                [classes.highlight]: selected.length > 0,
            })}
        >
            {!selected.length && (
                <Typography className={classes.title}>{title}</Typography>
            )}
            {selected.length > 0 && [
                <Typography className={classes.title}>
                    {selected.length} selected
                </Typography>,
                <Tooltip title='Delete'>
                    <IconButton
                        aria-label={`delete ${selected.length} selected items`}
                        data-cy='table-deleteSelected'
                        onClick={deleteSelected}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>,
            ]}
        </Toolbar>
        <TableContainer>
            <Table style={tableStyles}>
                <TableHead>
                    <TableRow>
                        <TableCell padding='checkbox'>
                            <Checkbox
                                disabled={!value.length}
                                indeterminate={
                                    selected.length > 0 &&
                                    selected.length < value.length
                                }
                                checked={
                                    value.length > 0 &&
                                    selected.length === value.length
                                }
                                onChange={handleSelectAllClick}
                                inputProps={{
                                    'aria-label': 'select all items',
                                }}
                            />
                        </TableCell>
                        {headers.map(item => (
                            <TableCell
                                className={dense ? classes.cells : null}
                                key={item.key}
                                align={item.align || 'right'}
                            >
                                {item.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {value.map((item, index) => {
                        const rowIsSelected = isSelected(index);
                        return (
                            <TableRow
                                hover
                                role='checkbox'
                                onClick={event => handleRowClick(event, index)}
                                aria-checked={rowIsSelected}
                                key={`table-row-${index + 1}`}
                                selected={rowIsSelected}
                            >
                                <TableCell padding='checkbox'>
                                    <Checkbox
                                        checked={rowIsSelected}
                                        aria-label={`row ${index + 1}`}
                                        data-cy={`table-row-${index + 1}`}
                                    />
                                </TableCell>
                                {headers.map(header => {
                                    let value = item[header.key];
                                    if (value === true || value === false) {
                                        value = value ? 'Yes' : 'No';
                                    }
                                    if (header.type === 'date') {
                                        value = moment(value).format("MMM Do 'YY");
                                    }
                                    return (
                                        <TableCell
                                            className={dense ? classes.cells : null}
                                            align={header.align || 'right'}
                                        >
                                            {value}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                    <Show condition={!value.length}>
                        <TableRow>
                            <TableCell colSpan={headers.length + 1} align='center'>
                                <Typography variant='caption'>
                                    No data available...
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </Show>
                    {children}
                </TableBody>
            </Table>
        </TableContainer>
    </>;
};

export default FancyTable;
